/* Lens Note ✦ — record selfie video + handwriting in one shot
 *
 * Four screens (mirroring the supplied concept):
 *   1. INTRO    — editorial card with "Leave a Lens Note ✦"
 *   2. RECORD   — camera preview + transparent drawing canvas + REC
 *   3. PREVIEW  — play/pause + retake + save
 *   4. SAVED    — "Your note now lives inside tonight ✦" → wall
 *
 * Composition strategy (client-side, locked iter 167):
 *   • One offscreen <canvas> draws each video frame + active strokes
 *     via requestAnimationFrame.
 *   • That canvas's stream + the user's mic stream are merged into a
 *     single MediaStream, fed into MediaRecorder. The recorder
 *     produces a single composited MP4/WebM blob with handwriting
 *     baked in — no server-side ffmpeg pass required for MVP.
 *   • Stroke vectors (with timestamps) are persisted alongside the
 *     blob so a future ffmpeg re-render can regenerate higher-
 *     quality output without re-recording the moment.
 *
 * Tone: walnut / ivory / gold. Editorial. No SaaS / TikTok chrome.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import {
  LENS_CANVAS_W, LENS_CANVAS_H, containFitVideoIntoCanvas,
} from '../../lib/booth-os/lensCoordinateSpaces';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INK = '#0c0805';
const IVORY = '#f5efdf';
const IVORY_SOFT = '#d8cfb7';
const GOLD = '#c19a4b';
const DANGER = '#e0392a';

const MAX_DURATION_S = 30.0;
// iter202 — Phase B kickoff. Camera acquisition now goes through
// the unified useBoothCameraPipeline (profile: 'lens' — the only
// `mirror-export` consumer in the platform). The composite +
// overlay + MediaRecorder pipeline below is PRESERVED byte-for-
// byte: it is the emotional payload (vector strokes + replay
// timing + stroke persistence + composition logic) and a rewrite
// would risk subtle handwriting/composition drift. Per Craig's
// iter201 directive: "migrate acquisition/framing/mirror into the
// pipeline BUT preserve the existing overlay composition/export
// system." This file is the boundary between those concerns.
//
// Canvas dimensions are imported from lensCoordinateSpaces.js so a
// single source of truth covers both the runtime layout AND the
// /debug/composition-language diagnostic route.
const PORTRAIT_W = LENS_CANVAS_W;
const PORTRAIT_H = LENS_CANVAS_H;
const STROKE_WIDTH = 12; // ink weight at 1080w — reads as ~4-5 CSS px
const RECORD_FPS = 30;
const RECORD_FRAME_MS = 1000 / RECORD_FPS;
const VIDEO_BITRATE = 8_000_000; // 8 Mbps — premium quality, aligns with Booth

const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

// ── Helpers ────────────────────────────────────────────────────
const fmtTime = (s) => {
  const t = Math.max(0, Math.floor(s));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

// Choose the best supported MediaRecorder mime type. iter179 — VP9
// first (best quality/size on Chrome + Android), VP8 next (broadest
// compatibility), then mp4 H.264 (Safari fallback). Matches the
// modern Booth OS codec priority.
const pickRecorderMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* keep trying */ }
  }
  return '';
};

// ── Editorial chrome ───────────────────────────────────────────
const Shell = ({ children, dataState }) => (
  <div
    data-testid="lens-note-page"
    data-state={dataState}
    style={{
      position: 'fixed', inset: 0, background: INK, color: IVORY,
      fontFamily: FONT_BODY, overflow: 'hidden',
    }}
  >{children}</div>
);

// ── 1. INTRO ───────────────────────────────────────────────────
function IntroScreen({ event, onStart, onCancel }) {
  return (
    <Shell dataState="intro">
      <div style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 480, margin: '0 auto' }}>
        <button
          onClick={onCancel}
          data-testid="lens-note-back"
          aria-label="Back"
          style={{
            alignSelf: 'flex-start', background: 'transparent', border: 'none',
            color: IVORY_SOFT, fontSize: 18, cursor: 'pointer', padding: 0,
          }}
        >‹</button>

        <div style={{ marginTop: 'auto', marginBottom: 'auto' }}>
          <h1
            data-testid="lens-note-title"
            style={{
              fontFamily: FONT_DISPLAY, fontSize: 44, lineHeight: 1.05,
              letterSpacing: '-0.015em', margin: 0, color: IVORY,
            }}
          >
            Leave a<br />Lens Note ✦
          </h1>
          <p
            style={{
              marginTop: 22, color: IVORY_SOFT, fontSize: 15.5, lineHeight: 1.65,
              maxWidth: 320,
            }}
          >
            Record a short video and write on the screen. Your message will live
            on this memory ✦
          </p>

          <div
            data-testid="lens-note-tip"
            style={{
              marginTop: 28, padding: '14px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}
          >
            <span style={{ fontSize: 16, color: GOLD }}>💡</span>
            <div>
              <p style={{ margin: 0, fontSize: 12, color: GOLD, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700 }}>
                Tip
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 13.5, color: IVORY_SOFT, lineHeight: 1.5 }}>
                Write on the screen while you record!
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <button
            onClick={onStart}
            data-testid="lens-note-start-button"
            style={{
              width: '100%', padding: '16px 18px', borderRadius: 999,
              border: 'none', cursor: 'pointer',
              background: `linear-gradient(180deg, ${GOLD}, #9a7836)`,
              color: '#1a0f04', fontWeight: 700, letterSpacing: '0.04em',
              fontSize: 15, fontFamily: FONT_BODY,
            }}
          >
            Start Recording ✦
          </button>
          <button
            onClick={onCancel}
            data-testid="lens-note-cancel"
            style={{
              marginTop: 14, width: '100%', padding: '12px 18px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: IVORY_SOFT, fontSize: 14, fontFamily: FONT_BODY,
            }}
          >Cancel</button>
        </div>

        {event && (
          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em' }}>
            for {event.couple_names || event.event_name || 'tonight'} ✦
          </p>
        )}
      </div>
    </Shell>
  );
}

// ── 2. RECORDING ───────────────────────────────────────────────
// Captures: camera stream + canvas stream + mic. Draws video frames +
// active strokes onto the composition canvas at ~30fps. MediaRecorder
// records that canvas stream so handwriting is permanently baked in.
function RecordingScreen({ onDone, onCancel }) {
  // iter202 — Camera acquisition delegated to the booth camera
  // pipeline. The pipeline owns: stream acquisition, profile-driven
  // constraints (lens profile = 4:3 / 1280x960 / front-facing /
  // `mirror-export` mode), interruption recovery (track.onended),
  // long-session resilience (visibilitychange / pagehide /
  // beforeunload), and dimensionsStable signalling so we can defer
  // the rAF compositor until the video's intrinsic dimensions
  // actually settle.
  //
  // Everything BELOW the cam handle is unchanged from iter181 —
  // composite canvas, overlay canvas, drawFrame mirror math,
  // MediaRecorder pipeline, stroke persistence, poster snapshot.
  // That subsystem is the emotional payload and must remain stable.
  const cam = useBoothCameraPipeline({ profile: 'lens' });
  const videoRef = cam.videoRef;
  const compositeCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  // iter181 — Poster snapshot canvas. drawFrame's fillRect('#000') +
  // drawImage(video) sequence means there's a ~16ms window inside
  // each frame where the composite is solid black. If stopAll
  // cancels rAF after one final partial frame (or the camera track
  // dies before the next drawImage lands), and `rec.onstop`'s
  // toBlob fires AFTER that, the poster captures pure black. We
  // sidestep the race by snapshotting the composite into this
  // separate canvas SYNCHRONOUSLY inside stopAll, before any
  // teardown — guaranteeing we always have the last good frame.
  const posterCanvasRef = useRef(null);
  const rafRef = useRef(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const strokesRef = useRef([]);
  const activeStrokeRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);
  const [penOn, setPenOn] = useState(true);

  const stopAll = useCallback(() => {
    // iter181 — snapshot the LAST KNOWN GOOD composite frame into a
    // hidden poster canvas BEFORE we cancel rAF / kill camera tracks.
    // This is the only reliable moment where we know the composite
    // contains a real video frame (not the mid-frame black fill).
    try {
      const cc = compositeCanvasRef.current;
      if (cc && cc.width && cc.height) {
        const pc = posterCanvasRef.current || document.createElement('canvas');
        pc.width = cc.width;
        pc.height = cc.height;
        pc.getContext('2d').drawImage(cc, 0, 0);
        posterCanvasRef.current = pc;
      }
    } catch { /* poster is enrichment — never let it block teardown */ }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    try { recorderRef.current?.stop(); } catch { /* idempotent */ }
    // iter202 — Pipeline owns stream lifecycle; cam.stop() releases
    // tracks, nulls srcObject, transitions status. The local
    // camStreamRef is gone — we read the active stream through
    // cam.streamRef.current when we need audio tracks.
    try { cam.stop(); } catch { /* idempotent */ }
  }, [cam]);

  // Iter172 — the previous "overlay-dirty" optimization was wrong:
  // every frame the video drawImage repaints the entire composite
  // canvas, which wipes any previously composited strokes. Skipping
  // the overlay redraw between pen movements caused handwriting to
  // disappear from the recorded MP4 the moment the user lifted their
  // finger. The overlay canvas is small (≤720×1280) and persists
  // strokes itself, so unconditionally compositing it per frame is
  // cheap and correct.

  // Boot: pipeline.start() → composite canvas → MediaRecorder
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // iter202 — Stream acquisition through the booth camera
        // pipeline. The 'lens' profile selects mirror-export mode
        // and the front-facing camera with audio on. The pipeline
        // attaches the stream to videoRef and surfaces dimensions
        // stability through cam.dimensionsStable.
        const stream = await cam.start();
        if (cancelled) { try { cam.stop(); } catch {} return; }
        if (!stream) {
          setError('Couldn’t reach the camera tonight ✦');
          return;
        }
        const v = videoRef.current;
        // The pipeline already set srcObject + muted + called
        // play(); these next two lines guard against the pipeline
        // racing the cancelled flag in edge cases.
        v.muted = true;
        v.playsInline = true;

        // Composite canvas — locked to PORTRAIT_W × PORTRAIT_H
        // (1080×1920). Single source of truth: the same surface is
        // the live preview, the MediaRecorder source and the
        // exported MP4. We NEVER inherit raw camera dimensions
        // (which would be landscape on most webcams).
        const cc = compositeCanvasRef.current;
        cc.width = PORTRAIT_W;
        cc.height = PORTRAIT_H;
        const ctx = cc.getContext('2d', { alpha: false });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Overlay canvas — also locked to PORTRAIT_W × PORTRAIT_H so
        // it composites 1:1 onto the composite (no per-frame scaling).
        const ovInit = overlayCanvasRef.current;
        if (ovInit) {
          ovInit.width = PORTRAIT_W;
          ovInit.height = PORTRAIT_H;
        }

        // 30fps throttle gate.
        let lastDrawnAt = 0;

        const drawFrame = (now) => {
          if (now - lastDrawnAt < RECORD_FRAME_MS - 1) {
            rafRef.current = requestAnimationFrame(drawFrame);
            return;
          }
          lastDrawnAt = now;

          // Cover→Contain (iter179, Booth-aligned): preserve the
          // FULL camera frame inside the portrait composite. No
          // digital zoom, no aggressive crop — the entire sensor FOV
          // stays visible, centred, with calm black bands above/below
          // when the sensor is landscape. Mirrored at compose time
          // for selfie convention (visible composite + export agree).
          const vw = v.videoWidth;
          const vh = v.videoHeight;
          if (vw && vh) {
            // CONTAIN math — scale the camera frame DOWN to fit
            // entirely within PORTRAIT_W × PORTRAIT_H.
            const sScale = Math.min(PORTRAIT_W / vw, PORTRAIT_H / vh);
            const dw = vw * sScale;
            const dh = vh * sScale;
            const dx = (PORTRAIT_W - dw) / 2;
            const dy = (PORTRAIT_H - dh) / 2;
            ctx.save();
            ctx.translate(PORTRAIT_W, 0);
            ctx.scale(-1, 1);
            // Fill the canvas with black first so the letterbox area
            // is a calm solid black (matches Booth's stage feel).
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, PORTRAIT_W, PORTRAIT_H);
            try {
              ctx.drawImage(v, 0, 0, vw, vh, dx, dy, dw, dh);
            } catch { /* video not ready */ }
            ctx.restore();
          }

          // Overlay — mirrored with the same transform so handwriting
          // stays aligned to the mirrored face AND reads correctly.
          // (pointerPos stores strokes pre-flipped relative to the
          // mirrored composite, so this final X-flip on composite
          // brings them back to readable orientation.)
          const ov = overlayCanvasRef.current;
          if (ov) {
            ctx.save();
            ctx.translate(PORTRAIT_W, 0);
            ctx.scale(-1, 1);
            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(ov, 0, 0);
            ctx.restore();
          }
          rafRef.current = requestAnimationFrame(drawFrame);
        };
        rafRef.current = requestAnimationFrame(drawFrame);

        // Build the recorded stream: canvas video + mic audio
        const canvasStream = cc.captureStream(RECORD_FPS);
        const mic = stream.getAudioTracks();
        if (mic.length) canvasStream.addTrack(mic[0]);

        const mime = pickRecorderMime();
        const opts = mime
          ? { mimeType: mime, videoBitsPerSecond: VIDEO_BITRATE }
          : { videoBitsPerSecond: VIDEO_BITRATE };
        const rec = new MediaRecorder(canvasStream, opts);
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
        rec.onstop = () => {
          const finalMime = mime || rec.mimeType || 'video/webm';
          const blob = new Blob(chunksRef.current, { type: finalMime });
          // Iter171: hand the blob off IMMEDIATELY so the UI can
          // transition to "Preserving your note ✦". The poster
          // toBlob runs async — the destination handler treats it
          // as an optional enrichment, never blocks the save.
          // iter181 — prefer the snapshot canvas captured inside
          // stopAll (pre-teardown). It always contains the last
          // good composite frame. Fall back to the live composite
          // canvas only if the snapshot somehow wasn't taken.
          const posterSrc = posterCanvasRef.current || cc;
          onDone({
            blob,
            mimeType: finalMime,
            durationSeconds: (performance.now() - startTimeRef.current) / 1000,
            strokes: strokesRef.current,
            poster: null,           // arrives via posterPromise below
            posterPromise: new Promise((resolve) => {
              try {
                posterSrc.toBlob((b) => resolve(b || null), 'image/jpeg', 0.85);
              } catch { resolve(null); }
            }),
          });
        };
        recorderRef.current = rec;

        // Auto-start the recording immediately — the spec is clear: this
        // screen IS the recording state. The user already opted in by
        // tapping "Start Recording ✦" on the intro.
        chunksRef.current = [];
        strokesRef.current = [];
        startTimeRef.current = performance.now();
        rec.start(250);
        setRecording(true);

      } catch (e) {
        console.warn('[lens-note] getUserMedia failed', e);
        setError(e?.message || "Couldn't reach the camera tonight ✦");
      }
    })();
    return () => { cancelled = true; stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick the elapsed counter + enforce the 30s hard cap
  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => {
      const t = (performance.now() - startTimeRef.current) / 1000;
      setElapsed(t);
      if (t >= MAX_DURATION_S) {
        clearInterval(id);
        setRecording(false);
        try { recorderRef.current?.stop(); } catch { /* idempotent */ }
      }
    }, 100);
    return () => clearInterval(id);
  }, [recording]);

  // Drawing: pointer events come from the visible composite canvas.
  // The composite is selfie-mirrored at compose time (drawFrame
  // X-flips both video and overlay), so screen and composite share
  // the same coordinate system visually. pointerPos still flips X
  // when storing into the overlay so that the final compose-time
  // X-flip lands strokes back at the touch position.
  const overlayCtx = () => overlayCanvasRef.current?.getContext('2d');
  const applyInkStyle = (ctx) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255,253,247,0.96)';
    ctx.lineWidth = STROKE_WIDTH;
    ctx.shadowColor = 'rgba(255,255,255,0.35)';
    ctx.shadowBlur = 8;
  };
  const pointerPos = (e) => {
    // Use the visible composite canvas as the hit-test surface — it
    // covers the same screen area as the touch element.
    const cc = compositeCanvasRef.current;
    if (!cc) return { x: 0, y: 0, pressure: 0.5 };
    const rect = cc.getBoundingClientRect();
    const xScale = PORTRAIT_W / rect.width;
    const yScale = PORTRAIT_H / rect.height;
    // The composite is pre-mirrored at compose time (drawFrame
    // X-flips both video and overlay). We store strokes in the
    // overlay at the FLIPPED X position so that the final flip
    // during compose brings them back to the touch position.
    return {
      x: (rect.right - e.clientX) * xScale,
      y: (e.clientY - rect.top) * yScale,
      pressure: (e.pressure && e.pressure > 0) ? e.pressure : 0.5,
    };
  };
  const startStroke = (e) => {
    if (!penOn || !recording) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = pointerPos(e);
    const stroke = {
      started_at: performance.now() - startTimeRef.current,
      ended_at: null,
      points: [[p.x, p.y, 0, p.pressure]],
    };
    activeStrokeRef.current = stroke;
    strokesRef.current.push(stroke);
    const ctx = overlayCtx();
    applyInkStyle(ctx);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const moveStroke = (e) => {
    if (!penOn || !recording || !activeStrokeRef.current) return;
    const p = pointerPos(e);
    const t = (performance.now() - startTimeRef.current) - activeStrokeRef.current.started_at;
    const points = activeStrokeRef.current.points;
    const prev = points[points.length - 1];
    points.push([p.x, p.y, t, p.pressure]);
    // Quadratic mid-point smoothing — Apple-Pencil / Snapchat-style
    // glass ink. Curve from the previous segment's end to the
    // midpoint of (prev → curr), using prev as the control point.
    const ctx = overlayCtx();
    if (prev) {
      const mx = (prev[0] + p.x) / 2;
      const my = (prev[1] + p.y) / 2;
      ctx.quadraticCurveTo(prev[0], prev[1], mx, my);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(mx, my);
    }
  };
  const endStroke = () => {
    if (!activeStrokeRef.current) return;
    // Tail-in to the final raw point so the stroke ends cleanly.
    const points = activeStrokeRef.current.points;
    const last = points[points.length - 1];
    if (last) {
      const ctx = overlayCtx();
      ctx.lineTo(last[0], last[1]);
      ctx.stroke();
    }
    activeStrokeRef.current.ended_at = performance.now() - startTimeRef.current;
    activeStrokeRef.current = null;
  };

  const undoLastStroke = () => {
    strokesRef.current.pop();
    const ov = overlayCanvasRef.current;
    if (!ov) return;
    const ctx = ov.getContext('2d');
    ctx.clearRect(0, 0, ov.width, ov.height);
    applyInkStyle(ctx);
    // Re-render each stroke with the same quadratic smoothing.
    for (const s of strokesRef.current) {
      const pts = s.points;
      if (!pts.length) continue;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2;
        ctx.quadraticCurveTo(a[0], a[1], mx, my);
      }
      const last = pts[pts.length - 1];
      ctx.lineTo(last[0], last[1]);
      ctx.stroke();
    }
  };

  // (No sizeOverlay needed — the overlay canvas is locked to
  // PORTRAIT_W × PORTRAIT_H at boot. Single source of truth.)

  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    try { recorderRef.current?.stop(); } catch { /* idempotent */ }
  };

  if (error) {
    return (
      <Shell dataState="error">
        <div style={{ padding: 28, textAlign: 'center', marginTop: '40vh' }}>
          <p style={{ color: IVORY_SOFT, fontFamily: FONT_DISPLAY, fontSize: 22 }}>
            We couldn't reach the camera tonight ✦
          </p>
          <p style={{ color: IVORY_SOFT, fontSize: 13, opacity: 0.8 }}>{error}</p>
          <button onClick={onCancel} data-testid="lens-note-error-back" style={{
            marginTop: 18, padding: '10px 18px', borderRadius: 999, border: 'none',
            background: GOLD, color: '#1a0f04', fontWeight: 700, cursor: 'pointer',
          }}>Back</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell dataState="recording">
      {/* Raw camera <video> — hidden. Drives the camera stream into
          the composite canvas. We never render it directly.
          Single source of truth = the composite canvas below. */}
      <video
        ref={videoRef} playsInline muted
        data-testid="lens-note-camera-preview"
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      {/* Visible composite canvas — IS the live preview AND the
          MediaRecorder source. 9:16 (1080×1920) is enforced inside
          a fixed aspect-ratio frame so the live preview shows EXACTLY
          what the exported MP4 will contain — no zoom jump on save.
          Selfie mirroring happens AT COMPOSE TIME inside drawFrame
          (both video and overlay are X-flipped). The visible composite
          has NO CSS transform — its pixels are the export pixels,
          and what the user sees while recording is byte-identical
          to what they see during playback. */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#000',
      }}>
        <div style={{
          position: 'relative',
          aspectRatio: '9 / 16',
          // Pattern: width:100% fills horizontally, aspect-ratio
          // derives the height. If derived height would exceed
          // parent (e.g. on mobile viewports taller than 9:16
          // works fine, but on desktop wider than 9:16 the
          // derived height is huge), max-height:100% clamps it
          // and the browser shrinks width proportionally to
          // preserve aspect-ratio. Result: WYSIWYG box on any
          // viewport, always exactly 9:16.
          width: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
        }}>
          <canvas
            ref={compositeCanvasRef}
            data-testid="lens-note-composite-canvas"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              // No CSS transform — the composite is already
              // selfie-mirrored at compose time (video + overlay
              // both X-flipped by drawFrame). Live preview and
              // export see identical pixels.
              display: 'block',
            }}
          />
          {/* Drawing surface — fully transparent overlay sized to the
              same aspect frame. Touch events are captured here and
              translated into composite/overlay-canvas coordinates by
              pointerPos (which un-mirrors X). The overlay canvas
              itself is offscreen and 1080×1920; this transparent
              <canvas> is just the hit-test surface. */}
          <canvas
            data-testid="lens-note-drawing-canvas"
            onPointerDown={startStroke}
            onPointerMove={moveStroke}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              touchAction: 'none',
              background: 'transparent',
            }}
          />
        </div>
      </div>
      {/* Offscreen ink canvas — accumulates strokes. Blitted into
          the composite each frame by drawFrame. */}
      <canvas ref={overlayCanvasRef} style={{ display: 'none' }} />

      {/* Top — recording timer */}
      <div style={{
        position: 'absolute', top: 'env(safe-area-inset-top, 20px)', left: 0, right: 0,
        display: 'flex', justifyContent: 'center', zIndex: 5,
      }}>
        <div
          data-testid="lens-note-timer"
          style={{
            padding: '6px 14px', background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(8px)', borderRadius: 999,
            display: 'flex', alignItems: 'center', gap: 8,
            fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700,
            color: IVORY, letterSpacing: '0.04em',
          }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: 999, background: DANGER,
            animation: recording ? 'lensNoteBlink 1.2s ease-in-out infinite' : 'none',
          }} />
          {fmtTime(elapsed)}
        </div>
      </div>

      {/* Bottom — pen toggle · record · undo */}
      <div style={{
        position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 20px) + 28px)',
        left: 0, right: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', padding: '0 28px',
        zIndex: 5,
      }}>
        <button
          onClick={() => setPenOn((v) => !v)}
          data-testid="lens-note-pen-toggle"
          aria-label="Toggle pen"
          style={{
            width: 48, height: 48, borderRadius: 999,
            background: penOn ? IVORY : 'rgba(255,255,255,0.18)',
            color: penOn ? INK : IVORY,
            border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700,
          }}
        >✎</button>

        <button
          onClick={stopRecording}
          data-testid="lens-note-stop-button"
          aria-label="Stop recording"
          style={{
            width: 78, height: 78, borderRadius: 999,
            background: '#fff', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 4px rgba(255,255,255,0.18), 0 0 0 8px rgba(255,255,255,0.08)',
          }}
        >
          <span style={{ width: 28, height: 28, background: DANGER, borderRadius: 4, display: 'block' }} />
        </button>

        <button
          onClick={undoLastStroke}
          data-testid="lens-note-undo"
          aria-label="Undo last stroke"
          style={{
            width: 48, height: 48, borderRadius: 999,
            background: 'rgba(255,255,255,0.18)', color: IVORY,
            border: 'none', cursor: 'pointer', fontSize: 22,
          }}
        >↶</button>
      </div>

      <style>{`@keyframes lensNoteBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }`}</style>
    </Shell>
  );
}

// ── 3. PREVIEW ────────────────────────────────────────────────
function PreviewScreen({ takeUrl, durationSeconds, onRetake, onSave, saving, error }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  return (
    <Shell dataState="preview">
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '12px 16px 24px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', marginBottom: 16 }}>
          <button
            onClick={onRetake}
            data-testid="lens-note-preview-back"
            aria-label="Back"
            style={{ position: 'absolute', left: 0, background: 'transparent', border: 'none', color: IVORY_SOFT, fontSize: 18, cursor: 'pointer' }}
          >‹</button>
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 15, fontFamily: FONT_DISPLAY, fontStyle: 'italic' }}>Preview</p>
            <p style={{ margin: 0, fontSize: 12, color: IVORY_SOFT, letterSpacing: '0.06em' }}>{fmtTime(durationSeconds)}</p>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderRadius: 14, overflow: 'hidden' }}>
          {/* 9:16 aspect frame — matches the exported MP4 exactly,
              so the playback shows the SAME framing as the recording.
              No more huge black bands. */}
          <div style={{
            position: 'relative',
            aspectRatio: '9 / 16',
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
          }}>
            {takeUrl && (
              <video
                ref={videoRef}
                src={takeUrl}
                playsInline
                controls={false}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onClick={() => { const v = videoRef.current; if (!v) return; v.paused ? v.play() : v.pause(); }}
                data-testid="lens-note-preview-video"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <button
            onClick={() => { const v = videoRef.current; if (!v) return; v.paused ? v.play() : v.pause(); }}
            data-testid="lens-note-preview-toggle"
            aria-label="Play / pause"
            style={{
              width: 44, height: 44, borderRadius: 999, border: 'none',
              background: 'rgba(255,255,255,0.12)', color: IVORY, cursor: 'pointer', fontSize: 18,
            }}
          >{playing ? '❚❚' : '▶'}</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12, marginTop: 18 }}>
          <button
            onClick={onRetake}
            data-testid="lens-note-retake"
            disabled={saving}
            style={{
              padding: '14px 16px', borderRadius: 999, border: 'none', cursor: saving ? 'default' : 'pointer',
              background: 'rgba(255,255,255,0.08)', color: IVORY, fontSize: 14, fontWeight: 700,
              opacity: saving ? 0.5 : 1,
            }}
          >Retake</button>
          <button
            onClick={onSave}
            data-testid="lens-note-save"
            disabled={saving}
            style={{
              padding: '14px 16px', borderRadius: 999, border: 'none', cursor: saving ? 'default' : 'pointer',
              background: `linear-gradient(180deg, ${GOLD}, #9a7836)`, color: '#1a0f04',
              fontWeight: 700, fontSize: 14, opacity: saving ? 0.5 : 1,
            }}
          >{saving ? 'Saving…' : 'Save Lens Note ✦'}</button>
        </div>

        {error && (
          <p
            data-testid="lens-note-save-error"
            style={{
              marginTop: 14, textAlign: 'center',
              fontSize: 13, fontStyle: 'italic',
              color: '#f4b8b0',
            }}
          >
            {error}
          </p>
        )}
      </div>
    </Shell>
  );
}

// ── 4. SAVED ──────────────────────────────────────────────────
function SavedScreen({ event }) {
  return (
    <Shell dataState="saved">
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 28 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>✦ Held</p>
          <p
            data-testid="lens-note-saved-message"
            style={{ fontFamily: FONT_DISPLAY, fontSize: 30, lineHeight: 1.2, marginTop: 16, color: IVORY }}
          >
            Your note now lives inside tonight ✦
          </p>
          <p style={{ marginTop: 14, color: IVORY_SOFT, fontSize: 13.5 }}>
            Carrying you to the wall…
          </p>
        </div>
      </div>
    </Shell>
  );
}

// ── Page wrapper ──────────────────────────────────────────────
export default function LensNotePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // iter210 ✦ — kiosk awareness. When the booth-os shell launches
  // the standalone Lens Notes page it appends ?kiosk=1 so the
  // Personal Memory Page renders the "Pass to the next memory ✦"
  // gesture for the next guest. Personal-device QR scans omit the
  // param and land in the calm single-guest view.
  const [searchParams] = useSearchParams();
  const isKiosk = searchParams.get('kiosk') === '1';
  const [stage, setStage] = useState('intro');
  const [event, setEvent] = useState(null);
  const [take, setTake] = useState(null);  // { blob, mimeType, durationSeconds, strokes, poster }
  const [takeUrl, setTakeUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Resolve event (slug → event) for the editorial header line.
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/events/slug/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null));
  }, [slug]);

  // Object URL for preview playback — revoke on change/unmount
  useEffect(() => {
    if (!take?.blob) return undefined;
    const url = URL.createObjectURL(take.blob);
    setTakeUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [take?.blob]);

  const onRecorded = (t) => {
    setTake(t);
    setStage('preview');
  };

  const onRetake = () => {
    setTake(null);
    setTakeUrl('');
    setStage('recording');
  };

  const onSave = async () => {
    // iter211 production fix — the previous guard required `event` to
    // be resolved, but the upload endpoint only needs the slug
    // (which is always present from useParams). On production the
    // event lookup could be slow / fail silently and the Save
    // button appeared dead because this no-op fired before any
    // network call. We only block on the two real prerequisites:
    // the take (recorded blob) and an in-flight save.
    if (!take || saving) {
      // eslint-disable-next-line no-console
      console.warn('[lens-note] save blocked', { hasTake: !!take, saving });
      return;
    }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('event_slug', slug);
      // Defensive: durationSeconds is always set by the recorder,
      // but a future codec change or interrupted capture could
      // leave it undefined. Coerce so .toFixed never throws.
      const dur = Number(take.durationSeconds);
      fd.append('duration_seconds', String((Number.isFinite(dur) ? dur : 0).toFixed(2)));
      fd.append('stroke_data', JSON.stringify(take.strokes || []));
      const ext = (take.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
      fd.append('mp4', new File([take.blob], `lens-note.${ext}`, { type: take.mimeType || 'video/webm' }));
      // Iter171: the poster blob arrives via posterPromise so the
      // recording-stop transition can render instantly. If the encode
      // hasn't finished by the time the user taps Save (extremely
      // unlikely — the preview screen alone takes longer than poster
      // encoding), we wait briefly; otherwise we ship without it.
      let posterBlob = take.poster;
      if (!posterBlob && take.posterPromise) {
        posterBlob = await Promise.race([
          take.posterPromise,
          new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
        ]);
      }
      if (!posterBlob) posterBlob = new Blob([], { type: 'image/jpeg' });
      fd.append('poster', new File([posterBlob], 'poster.jpg', { type: 'image/jpeg' }));
      const resp = await axios.post(`${API}/lens-notes/upload`, fd);
      setStage('saved');
      // iter210 ✦ — Personal Memory Return Path. Land the guest on
      // their own contribution (kiosk-aware), never the Memory Wall.
      const contributionId = resp?.data?.lens_note_id;
      setTimeout(() => {
        if (contributionId) {
          navigate(
            `/event/${slug}/m/${contributionId}${isKiosk ? '?kiosk=1' : ''}`,
          );
        } else {
          // Defensive fallback: if the upload response somehow
          // omitted the id, return to the Memory Wall rather than
          // strand the guest on the "saved" screen.
          navigate(`/event/${slug}/memory-wall`);
        }
      }, 1700);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[lens-note] save failed', e);
      setSaving(false);
      setError(
        e?.response?.data?.detail
        || e?.message
        || 'Could not save the note tonight ✦',
      );
    }
  };

  const cancel = () => navigate(`/event/${slug}/memory-wall`);

  if (stage === 'intro')     return <IntroScreen event={event} onStart={() => setStage('recording')} onCancel={cancel} />;
  if (stage === 'recording') return <RecordingScreen onDone={onRecorded} onCancel={() => setStage('intro')} />;
  if (stage === 'preview')   return <PreviewScreen takeUrl={takeUrl} durationSeconds={take?.durationSeconds || 0} onRetake={onRetake} onSave={onSave} saving={saving} error={error} />;
  if (stage === 'saved')     return <SavedScreen event={event} />;
  return null;
}
